import { useMemo, useState } from 'react';
import { MY_PICTURES_PHOTOS } from '../../data/myPicturesPhotos';

const FALLBACK_TEXT = 'Preview unavailable. Add image files to /public/photos/nostalgia/.';

export const MyPicturesApp = () => {
  const [selectedPhotoId, setSelectedPhotoId] = useState(MY_PICTURES_PHOTOS[0]?.id || null);
  const [viewerMode, setViewerMode] = useState('thumbnails');
  const [brokenPhotos, setBrokenPhotos] = useState({});

  const selectedIndex = useMemo(
    () => MY_PICTURES_PHOTOS.findIndex((photo) => photo.id === selectedPhotoId),
    [selectedPhotoId]
  );

  const selectedPhoto = selectedIndex >= 0 ? MY_PICTURES_PHOTOS[selectedIndex] : null;
  const canGoPrev = selectedIndex > 0;
  const canGoNext = selectedIndex >= 0 && selectedIndex < MY_PICTURES_PHOTOS.length - 1;

  const goToIndex = (idx) => {
    const nextPhoto = MY_PICTURES_PHOTOS[idx];
    if (!nextPhoto) return;
    setSelectedPhotoId(nextPhoto.id);
    setViewerMode('viewer');
  };

  const handlePrev = () => {
    if (!canGoPrev) return;
    goToIndex(selectedIndex - 1);
  };

  const handleNext = () => {
    if (!canGoNext) return;
    goToIndex(selectedIndex + 1);
  };

  const markBroken = (id) => {
    setBrokenPhotos((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <div className="my-pictures-app" data-testid="my-pictures-app">
      <div className="my-pictures-toolbar">
        <button className="xp-btn" type="button">Back</button>
        <button className="xp-btn" type="button">Search</button>
        <button className="xp-btn" type="button">Folders</button>
        <button className="xp-btn" type="button">Views</button>
        <div className="toolbar-sep" />
        <button
          className={`xp-btn ${viewerMode === 'thumbnails' ? 'active' : ''}`}
          type="button"
          onClick={() => setViewerMode('thumbnails')}
        >
          Thumbnails
        </button>
        <button
          className={`xp-btn ${viewerMode === 'viewer' ? 'active' : ''}`}
          type="button"
          onClick={() => setViewerMode('viewer')}
        >
          Preview
        </button>
        <div className="toolbar-spacer" />
        <span className="toolbar-path">My Documents\\My Pictures</span>
      </div>

      <div className="my-pictures-content">
        <aside className="my-pictures-sidebar">
          <div className="xp-panel">
            <div className="xp-panel-title">Picture Tasks</div>
            <ul>
              <li>View as a slide show</li>
              <li>Order prints online</li>
              <li>Print this picture</li>
              <li>Email this picture</li>
              <li>Burn pictures to CD</li>
            </ul>
          </div>

          <div className="xp-panel">
            <div className="xp-panel-title">File and Folder Tasks</div>
            <ul>
              <li>Make a new folder</li>
              <li>Publish this folder to the Web</li>
              <li>Share this folder</li>
            </ul>
          </div>

          <div className="xp-panel">
            <div className="xp-panel-title">Other Places</div>
            <ul>
              <li>My Documents</li>
              <li>My Computer</li>
              <li>My Network Places</li>
            </ul>
          </div>

          {selectedPhoto && (
            <div className="xp-panel details">
              <div className="xp-panel-title">Details</div>
              <div className="details-grid">
                <div>Name:</div>
                <div>{selectedPhoto.filename}</div>
                <div>Date:</div>
                <div>{selectedPhoto.date || 'Unknown'}</div>
                <div>Type:</div>
                <div>{selectedPhoto.type || 'Image File'}</div>
                <div>Size:</div>
                <div>{selectedPhoto.size || 'Unknown'}</div>
                <div>Dimensions:</div>
                <div>{selectedPhoto.dimensions || 'Unknown'}</div>
              </div>
            </div>
          )}
        </aside>

        <main className="my-pictures-main">
          <div className="my-pictures-preview-wrap">
            <div className="preview-toolbar">
              <button className="xp-btn" type="button" onClick={handlePrev} disabled={!canGoPrev}>Previous</button>
              <button className="xp-btn" type="button" onClick={handleNext} disabled={!canGoNext}>Next</button>
              {selectedPhoto && (
                <span className="preview-title">
                  {selectedPhoto.filename}
                </span>
              )}
            </div>

            <div className="preview-surface" onDoubleClick={() => setViewerMode('viewer')}>
              {selectedPhoto && !brokenPhotos[selectedPhoto.id] ? (
                <img
                  className="preview-image"
                  src={selectedPhoto.src}
                  alt={selectedPhoto.title || selectedPhoto.filename}
                  onError={() => markBroken(selectedPhoto.id)}
                />
              ) : (
                <div className="preview-fallback">{FALLBACK_TEXT}</div>
              )}
            </div>

            {selectedPhoto && (
              <div className="preview-caption">
                <strong>{selectedPhoto.title || selectedPhoto.filename}</strong>
                {selectedPhoto.description && <span>{selectedPhoto.description}</span>}
              </div>
            )}
          </div>

          <div className="thumbnail-strip" data-view={viewerMode}>
            {MY_PICTURES_PHOTOS.map((photo) => {
              const selected = photo.id === selectedPhotoId;
              return (
                <button
                  key={photo.id}
                  type="button"
                  className={`thumb-item ${selected ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedPhotoId(photo.id);
                    if (viewerMode === 'thumbnails') setViewerMode('viewer');
                  }}
                  onDoubleClick={() => {
                    setSelectedPhotoId(photo.id);
                    setViewerMode('viewer');
                  }}
                >
                  <div className="thumb-frame">
                    {!brokenPhotos[photo.id] ? (
                      <img
                        src={photo.src}
                        alt={photo.title || photo.filename}
                        onError={() => markBroken(photo.id)}
                      />
                    ) : (
                      <div className="thumb-fallback">IMG</div>
                    )}
                  </div>
                  <span className="thumb-name">{photo.filename}</span>
                </button>
              );
            })}
          </div>
        </main>
      </div>

      <div className="my-pictures-statusbar">
        <span>{MY_PICTURES_PHOTOS.length} object(s)</span>
        {selectedPhoto && <span>Selected: {selectedPhoto.filename}</span>}
      </div>
    </div>
  );
};

export default MyPicturesApp;
